import { has, isEmpty, isObject, mapValues, mergeWith, pick, reduce, uniq } from 'lodash';

export const addPrimitiveHintValue = <T extends string | number>(
  value: T,
  currentHints: T[],
  hintsLimit: number,
) => {
  if (!value || currentHints.includes(value)) {
    return currentHints;
  }

  return uniq([...currentHints, value]).slice(-hintsLimit);
};

export const addMultipleHintsValue = <T extends string[] | number[]>(
  value: T,
  currentHints: T,
  hintsLimit: number,
) => {
  if (isEmpty(value)) {
    return currentHints;
  }
  return uniq([...currentHints, ...value]).slice(-hintsLimit);
};

type HintsObject = Record<string, string[]>;

export const addMultipleHintsAsObject = <T extends HintsObject>(
  newHints: T,
  currentHints: T = {} as T,
  hintsLimit: number,
) => {
  const cleanedHints = mapValues(newHints, (hintsValues) => hintsValues.filter(Boolean));
  return mergeWith(
    currentHints,
    cleanedHints,
    (currentHintsValue: string[], newHintsValue: string[]) => {
      return addMultipleHintsValue(newHintsValue, currentHintsValue ?? [], hintsLimit);
    },
  );
};

export const filterHintsValue = (valueToRemove: string | { id: string }, hint: string) => {
  if (isObject(valueToRemove) && has(valueToRemove, 'id')) {
    return hint !== valueToRemove.id;
  }

  return hint !== valueToRemove;
};

export type PlainObjectHintToRemove = Record<string, string | { id: string }>;
export const removeHintFromObject = (
  hintValueToRemove: PlainObjectHintToRemove,
  objectHints: Record<string, string[]>,
) => {
  const fieldIdsToRemove = Object.keys(hintValueToRemove);
  const hintsToClenup = pick(objectHints, fieldIdsToRemove);

  return reduce(
    hintsToClenup,
    (cleanedHints, fieldHints, fieldId) => {
      const hintToRemove = hintValueToRemove[fieldId] as string | { id: string };
      const filteredValues = fieldHints.filter((hint: string) =>
        filterHintsValue(hintToRemove, hint),
      );

      return {
        ...cleanedHints,
        [fieldId]: filteredValues,
      };
    },
    {} as Record<string, string[]>,
  );
};
